import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Toaster } from './components/ui/sonner';
import MainLayout from './layouts/MainLayout';

// Pages
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import BuilderRegisterPage from './pages/BuilderRegisterPage';
import DashboardPage from './pages/DashboardPage';
import ContractorsPage from './pages/ContractorsPage';
import JobsPage from './pages/JobsPage';
import QuotesPage from './pages/QuotesPage';
import PurchaseOrdersPage from './pages/PurchaseOrdersPage';
import PricingPage from './pages/PricingPage';
import InvoicesPage from './pages/InvoicesPage';
import InvoicingPage from './pages/InvoicingPage';
import SchedulePage from './pages/SchedulePage';
import EmailsPage from './pages/EmailsPage';
import ReportsPage from './pages/ReportsPage';
import NextDayReportPage from './pages/NextDayReportPage';
import SettingsPage from './pages/SettingsPage';
import ClientPortalPage from './pages/ClientPortalPage';
import ClientPortalDashboard from './pages/ClientPortalDashboard';
import PaymentSuccessPage from './pages/PaymentSuccessPage';
import TradePortalPage from './pages/TradePortalPage';
import TradeOnboardPage from './pages/TradeOnboardPage';
import DocumentsPage from './pages/DocumentsPage';
import DocumentReviewPage from './pages/DocumentReviewPage';
import JobDetailPage from './pages/JobDetailPage';
import RecurringTemplatesPage from './pages/RecurringTemplatesPage';
import ImportDataPage from './pages/ImportDataPage';
import GPSTrackingPage from './pages/GPSTrackingPage';
import NotificationsPage from './pages/NotificationsPage';
import SuperAdminPage from './pages/SuperAdminPage';
import SuppliersPage from './pages/SuppliersPage';
import POInboxPage from './pages/POInboxPage';
import LandingPage from './pages/LandingPage';
import BookingCalendarPage from './pages/BookingCalendarPage';
import RemindersPage from './pages/RemindersPage';
import ActionListPage from './pages/ActionListPage';
import TradeInvoiceUploadPage from './pages/TradeInvoiceUploadPage';
import LearnedRatesPage from './pages/LearnedRatesPage';

import './App.css';

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/dashboard" replace />;
  }

  return <MainLayout>{children}</MainLayout>;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/register" element={<PublicRoute><RegisterPage /></PublicRoute>} />
      <Route path="/builder/register/:token" element={<PublicRoute><BuilderRegisterPage /></PublicRoute>} />
      <Route path="/client/quote/:token" element={<ClientPortalPage />} />
      
      {/* Client Portal - Public */}
      <Route path="/client" element={<ClientPortalDashboard />} />
      <Route path="/client/payment-success" element={<PaymentSuccessPage />} />
      <Route path="/client-portal" element={<Navigate to="/client" replace />} />
      
      {/* Trade Portal - Public */}
      <Route path="/trade" element={<TradePortalPage />} />
      <Route path="/trade-portal" element={<Navigate to="/trade" replace />} />
      <Route path="/trade-onboard/:token" element={<TradeOnboardPage />} />
      <Route path="/trade-invoice/:token" element={<TradeInvoiceUploadPage />} />

      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
      <Route path="/actions" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><ActionListPage /></ProtectedRoute>} />
      <Route path="/contractors" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><ContractorsPage /></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><SuppliersPage /></ProtectedRoute>} />
      <Route path="/po-inbox" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><POInboxPage /></ProtectedRoute>} />
      <Route path="/calendar" element={<ProtectedRoute><BookingCalendarPage /></ProtectedRoute>} />
      <Route path="/reminders" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><RemindersPage /></ProtectedRoute>} />
      <Route path="/jobs" element={<ProtectedRoute><JobsPage /></ProtectedRoute>} />
      <Route path="/jobs/:jobId" element={<ProtectedRoute><JobDetailPage /></ProtectedRoute>} />
      <Route path="/quotes" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><QuotesPage /></ProtectedRoute>} />
      <Route path="/purchase-orders" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><PurchaseOrdersPage /></ProtectedRoute>} />
      <Route path="/pricing" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><PricingPage /></ProtectedRoute>} />
      <Route path="/schedule-of-rates" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><LearnedRatesPage /></ProtectedRoute>} />
      <Route path="/invoices" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><InvoicesPage /></ProtectedRoute>} />
      <Route path="/invoicing" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><InvoicingPage /></ProtectedRoute>} />
      <Route path="/schedule" element={<ProtectedRoute><SchedulePage /></ProtectedRoute>} />
      <Route path="/emails" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><EmailsPage /></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><NextDayReportPage /></ProtectedRoute>} />
      <Route path="/reports-legacy" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><ReportsPage /></ProtectedRoute>} />
      <Route path="/documents" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><DocumentsPage /></ProtectedRoute>} />
      <Route path="/document-review" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><DocumentReviewPage /></ProtectedRoute>} />
      <Route path="/recurring-templates" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><RecurringTemplatesPage /></ProtectedRoute>} />
      <Route path="/import" element={<ProtectedRoute allowedRoles={['admin']}><ImportDataPage /></ProtectedRoute>} />
      <Route path="/gps-tracking" element={<ProtectedRoute allowedRoles={['admin', 'staff']}><GPSTrackingPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute allowedRoles={['admin']}><SettingsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute allowedRoles={['admin']}><SuperAdminPage /></ProtectedRoute>} />

      {/* Landing / Splash (public) */}
      <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
